import React, { useState, useEffect, useCallback } from 'react'
import { FormLabel, TextInput, emailRegex, Button, Checkbox, FormErrorMessage, SingleSelectField, SingleOption } from '@offerzen/pinata'
import { OptionalText } from './elements'
import { cx } from '@emotion/css'
import styles from './styles'

const validateEmail = value => {
  const isInputValid = emailRegex.test(value.trim())
  if (!isInputValid) return 'Please enter a valid work email address.'
  if (
    value.match(
      /.+@(gmail|hotmail|yahoo|live|yahoo|webmail|telkomsa|mweb|duck|icloud|alumni|msn|outlook|aol|comcast|libero.it|facebook)[a-z0-9_.+-]*\.[a-z0-9]+$/i,
    )
  )
    return 'This looks like a personal email address. Please use your work email address.'
  return null
}

const GatedContentForm = ({ setShowThankYouMessage }) => {
  const [buttonText, setButtonText] = useState('Get report')
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [company, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [pageVariantMeasureStart, setVariantMeasureStart] = useState('')
  const [offerzenInterest, setOfferzenInterest] = useState('')
  const [newsletterSub, setNewsletterSub] = useState(false)
  const [termsOfService, setTermsOfService] = useState(false)
  const [formFocused, setFormFocused] = useState(false)
  const [formErrorMessages, setFormErrorMessages] = useState({
    firstNameErrorMessage: '',
    lastNameErrorMessage: '',
    jobTitleErrorMessage: '',
    emailErrorMessage: '',
    companyErrorMessage: '',
    offerzenInterestErrorMessage: '',
    termsOfServiceError: '',
    submissionError: '',
  })

  const jobOptions = [
    { label: '- select -', value: '' },
    { label: "I'm a Founder/CEO", value: 'Founder/CEO' },
    { label: 'Technical Leader/VP', value: 'Technical Leader/VP' },
    { label: 'Engineering Manager', value: 'Engineering Manager' },
    { label: 'Recruitement Leader', value: 'Recruitement Leader' },
    { label: 'Recruiter', value: 'Recruiter' },
    { label: 'Other', value: 'Other' },
  ]

  const optInOptions = [
    { label: '- select -', value: '' },
    {
      label: "Yes, I'd like to try OfferZen",
      value: 'Product Access',
    },
    {
      label: "Yes, I'd like to see a demo of the platform",
      value: 'Call',
    },
    {
      label: "I'm already using OfferZen",
      value: 'Existing',
    },
    {
      label: 'No, not at this time',
      value: 'None',
    },
  ]

  // Not to be confused with react setState method
  // Created to reduce amount of code being duplicated with setting the different states
  const setErrorState = (errorMessageName, isValidName, message, isValid) => {
    setFormErrorMessages({
      ...formErrorMessages,
      [errorMessageName]: message,
    })
  }

  // Passing the ID manualy as the onBlur event does not always return a event object
  // This makes the handler unresponsive and could create a false positive
  const handleSelectionValidation = id => {
    if (id === 'job') {
      if (jobTitle === '') {
        setErrorState('jobTitleErrorMessage', 'isJobTitleValid', 'This field is required.', false)
      } else {
        setErrorState('jobTitleErrorMessage', 'isJobTitleValid', '', true)
      }
    } else {
      if (offerzenInterest === '') {
        setErrorState('offerzenInterestErrorMessage', 'isOfferzenInterestValid', 'This field is required.', false)
      } else {
        setErrorState('offerzenInterestErrorMessage', 'isOfferzenInterestValid', '', true)
      }
    }
  }

  const handleTextValidation = event => {
    const error = /^\s*[\w\s'-]+\s*$/.test(event.target.value.trim())
    if (event.target.id === 'firstname') {
      if (!error) {
        // setErrorState('lastNameErrorMessage', 'isLastNameValid', 'This field is required.', !error )
        setFormErrorMessages({
          ...formErrorMessages,
          firstNameErrorMessage: 'This field is required.',
        })
      } else {
        // setErrorState('lastNameErrorMessage', 'isLastNameValid', '', error )
        setFormErrorMessages({
          ...formErrorMessages,
          firstNameErrorMessage: '',
        })
      }
    }
    if (event.target.id === 'lastname') {
      if (!error) {
        // setErrorState('lastNameErrorMessage', 'isLastNameValid', 'This field is required.', !error )
        setFormErrorMessages({
          ...formErrorMessages,
          lastNameErrorMessage: 'This field is required.',
        })
      } else {
        // setErrorState('lastNameErrorMessage', 'isLastNameValid', '', error )
        setFormErrorMessages({
          ...formErrorMessages,
          lastNameErrorMessage: '',
        })
      }
    } else if (event.target.id === 'company') {
      if (!error) {
        // setErrorState('lastNameErrorMessage', 'isLastNameValid', 'This field is required.', !error )
        setFormErrorMessages({
          ...formErrorMessages,
          companyErrorMessage: 'This field is required.',
        })
      } else {
        // setErrorState('lastNameErrorMessage', 'isLastNameValid', '', error )
        setFormErrorMessages({
          ...formErrorMessages,
          companyErrorMessage: '',
        })
      }
    }
  }

  const handleEmailValidation = event => {
    const errorMessage = validateEmail(event.target.value)
    if (errorMessage == null) {
      setErrorState('emailErrorMessage', 'isEmailValid', '', true)
    } else {
      setErrorState('emailErrorMessage', 'isEmailValid', errorMessage, false)
    }
  }

  const handleCheckboxValidation = event => {
    if (event.target.id === 'accepted_terms_of_service') {
      if (event.target.value === 'false') {
        setErrorState('termsOfServiceError', 'isTermsOfServiceValid', 'This field is required', false)
      } else {
        setErrorState('termsOfServiceError', 'isTermsOfServiceValid', '', true)
      }
    }
  }

  const handlePageVariant = () => {
    if (!formFocused) {
      setVariantMeasureStart(btoa(new Date().getTime() / 1000))
      setFormFocused(true)
    }
  }

  const handleSubmission = useCallback(() => {
    setButtonDisabled(true)
    // Double checks that email is validated.
    // validateEmail returns null when valid else a string with the correct message
    if (firstName && lastName && company && email && !validateEmail(email) && termsOfService && jobTitle) {
      setFormErrorMessages({
        ...formErrorMessages,
        offerzenInterestErrorMessage: '',
        firstNameErrorMessage: '',
        lastNameErrorMessage: '',
        companyErrorMessage: '',
        jobTitleErrorMessage: '',
        emailErrorMessage: '',
        submissionError: '',
        termsOfServiceError: '',
      })

      submitForm()
    }

    if (firstName === '') {
      setFormErrorMessages(previousState => ({
        ...previousState,
        firstNameErrorMessage: 'This field is required.',
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }

    if (lastName === '') {
      setFormErrorMessages(previousState => ({
        ...previousState,
        lastNameErrorMessage: 'This field is required.',
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }

    if (email === '') {
      setFormErrorMessages(previousState => ({
        ...previousState,
        emailErrorMessage: 'This field is required.',
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }

    if (validateEmail(email)) {
      setFormErrorMessages(previousState => ({
        ...previousState,
        emailErrorMessage: validateEmail(email),
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }

    if (company === '') {
      setFormErrorMessages(previousState => ({
        ...previousState,
        companyErrorMessage: 'This field is required.',
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }

    if (jobTitle === '') {
      setFormErrorMessages(previousState => ({
        ...previousState,
        jobTitleErrorMessage: 'This field is required.',
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }

    if (offerzenInterest === '') {
      setFormErrorMessages(previousState => ({
        ...previousState,
        offerzenInterestErrorMessage: 'This field is required.',
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }

    if (!termsOfService) {
      setFormErrorMessages(previousState => ({
        ...previousState,
        termsOfServiceError: 'This field is required.',
        submissionError: 'Oops! Some info seems incorrect. Please review your info.',
      }))
      setButtonDisabled(false)
    }
  }, [firstName, lastName, email, formErrorMessages, termsOfService, company, jobTitle, offerzenInterest, buttonDisabled])

  useEffect(() => {
    if (firstName && lastName && company && email && termsOfService && jobTitle) {
      setFormErrorMessages(previousState => ({
        ...previousState,
        submissionError: '',
      }))
    }
  }, [firstName, lastName, email, company, termsOfService, jobTitle])

  const submitForm = useCallback(async () => {
    setButtonText('Submitting')
    const pageVariantMeasureEnd = btoa(new Date().getTime() / 1000)

    const payload = {
      firstName,
      lastName,
      email,
      company,
      jobTitle,
      offerzenInterest,
      newsletterSub,
      termsOfService,
      pageVariant: `${pageVariantMeasureStart}-${pageVariantMeasureEnd}`,
    }

    try {
      const submissionResult = await fetch(
        `${process.env.NODE_ENV === 'development' ? '' : process.env.NEXT_PUBLIC_BASE_URL}/api/marketingReport`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        },
      )

      const result = await submissionResult.json()

      if (submissionResult.status === 201) {
        setShowThankYouMessage(true)
      } else {
        setButtonDisabled(false)
        setButtonText('Get report')
        setFormErrorMessages(previousState => ({
          ...previousState,
          submissionError: result.error,
        }))
      }
    } catch (e) {
      setButtonDisabled(false)
      setButtonText('Get report')
      setFormErrorMessages(previousState => ({
        ...previousState,
        submissionError: 'Oops! Something went wrong. Please try again.',
      }))
    }
  }, [email, jobTitle, company, firstName, lastName, offerzenInterest, formErrorMessages, termsOfService, newsletterSub])

  return (
    <form className={styles.form} autoComplete="off" onFocus={handlePageVariant}>
      <FormLabel className={styles.label} label="First name" htmlFor="firstname" />

      <TextInput
        className={cx([styles.formInput, ...(formErrorMessages.firstNameErrorMessage !== '' ? [styles.inputError] : [])])}
        placeholder="Enter your first name"
        id="firstname"
        name="firstname"
        value={firstName}
        onChange={value => {
          if (formErrorMessages.firstNameErrorMessage) {
            setFormErrorMessages(previousState => ({
              ...previousState,
              firstNameErrorMessage: '',
            }))
          }
          setFirstName(value)
        }}
        onBlur={handleTextValidation}
        onKeyDown={event => {
          if (event.key === 'Tab') handleTextValidation(event)
        }}
        meta={{ touched: true, error: formErrorMessages.firstNameErrorMessage }}
      />

      <FormLabel className={styles.label} label="Last name" htmlFor="lastname" />

      <TextInput
        className={cx([styles.formInput, ...(formErrorMessages.lastNameErrorMessage !== '' ? [styles.inputError] : [])])}
        placeholder="Enter your last name"
        id="lastname"
        name="lastname"
        value={lastName}
        onChange={value => {
          if (formErrorMessages.lastNameErrorMessage) {
            setFormErrorMessages(previousState => ({
              ...previousState,
              lastNameErrorMessage: '',
            }))
          }
          setLastName(value)
        }}
        onBlur={handleTextValidation}
        onKeyDown={event => {
          if (event.key === 'Tab') handleTextValidation(event)
        }}
        meta={{ touched: true, error: formErrorMessages.lastNameErrorMessage }}
      />

      <FormLabel className={styles.label} label="Work email" htmlFor="work-email" />
      <OptionalText> - to send you the report</OptionalText>
      <TextInput
        className={cx([styles.formInput, ...(formErrorMessages.emailErrorMessage !== '' ? [styles.inputError] : [])])}
        placeholder="Name@company.com"
        id="work-email"
        name="email"
        value={email}
        onChange={value => {
          setEmail(value)
        }}
        onBlur={handleEmailValidation}
        onKeyDown={event => {
          if (event.key === 'Tab') handleEmailValidation(event)
        }}
        meta={{ touched: true, error: formErrorMessages.emailErrorMessage }}
      />

      <FormLabel className={styles.label} label="Company name" htmlFor="company" />

      <TextInput
        className={cx([styles.formInput, ...(formErrorMessages.companyErrorMessage !== '' ? [styles.inputError] : [])])}
        placeholder="Enter your company name"
        id="company"
        name="company"
        value={company}
        onChange={value => {
          if (formErrorMessages.companyErrorMessage) {
            setFormErrorMessages(previousState => ({
              ...previousState,
              companyErrorMessage: '',
            }))
          }
          setCompanyName(value)
        }}
        onBlur={handleTextValidation}
        onKeyDown={event => {
          if (event.key === 'Tab') handleTextValidation(event)
        }}
        meta={{ touched: true, error: formErrorMessages.companyErrorMessage }}
      />

      <FormLabel className={styles.label} label="Job title" htmlFor="job" />

      <SingleSelectField
        id="job"
        className={cx([styles.formInput, ...(formErrorMessages.jobTitleErrorMessage !== '' ? [styles.inputError] : [])])}
        name="job"
        value={jobTitle}
        meta={{ touched: true, error: formErrorMessages.jobTitleErrorMessage }}
        onChange={value => {
          setJobTitle(value)
          setErrorState('jobTitleErrorMessage', 'isJobTitleValid', value === '' ? 'This field is required.' : '', value === '')
        }}
        onBlur={() => handleSelectionValidation('job')}>
        {jobOptions.map(({ value, label }) => (
          <SingleOption key={value} label={label} value={value} />
        ))}
      </SingleSelectField>

      {
        // TODO re add the phone number field when a more stable version is available
        /* <PhoneNumber 
        label='Phone number'
        optionalText='optional'
        inputClassName={styles.phoneNumberField}
        labelClassName={styles.label}
        onChange={handlePhoneNumber}
      /> */
      }

      <FormLabel
        className={styles.label}
        label="Do you want to know more about using OfferZen to hire developers?"
        htmlFor="offerzen-interest"
      />

      <SingleSelectField
        id="offerzen-interest"
        name="offerzen_interest"
        className={cx([styles.formInput, ...(formErrorMessages.offerzenInterestErrorMessage !== '' ? [styles.inputError] : [])])}
        value={offerzenInterest}
        meta={{
          touched: true,
          error: formErrorMessages.offerzenInterestErrorMessage,
        }}
        onChange={value => {
          setOfferzenInterest(value)
          setErrorState(
            'offerzenInterestErrorMessage',
            'isOfferzenInterestValid',
            value === '' ? 'This field is required.' : '',
            value === '',
          )
        }}
        onBlur={() => handleSelectionValidation('offerzen-interest')}>
        {optInOptions.map(({ value, label }) => (
          <SingleOption key={value} label={label} value={value} />
        ))}
      </SingleSelectField>

      <Checkbox
        id="subscribe_to_company_newsletter"
        name="subscribe_to_company_newsletter"
        className={styles.checkBox}
        theme="primary"
        value={termsOfService}
        checked={newsletterSub}
        onBlur={handleCheckboxValidation}
        onChange={event => {
          setNewsletterSub(!newsletterSub)
        }}
        label={
          <span className={styles.checkBoxLabel}>
            Let me know about job-seeking developers and other marketplace insights, like data reports, events, and salary content.
            Unsubscribe at any time.
          </span>
        }
      />

      <Checkbox
        id="accepted_terms_of_service"
        name="accepted_terms_of_service"
        className={cx(styles.checkBox, styles.lastCheckbox)}
        theme="primary"
        value={termsOfService}
        checked={termsOfService}
        onBlur={handleCheckboxValidation}
        onChange={() => {
          if (formErrorMessages.termsOfServiceError) {
            setFormErrorMessages(previousState => ({
              ...previousState,
              termsOfServiceError: '',
            }))
          }
          setTermsOfService(!termsOfService)
        }}
        meta={{ touched: true, error: formErrorMessages.termsOfServiceError }}
        label={
          <span className={styles.checkBoxLabel}>
            I agree to OfferZen&apos;s{' '}
            <a href="https://www.offerzen.com/privacy" rel="noreferrer" target="_blank" className={styles.formCheckboxLink}>
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://www.offerzen.com/terms/employer" rel="noreferrer" target="_blank" className={styles.formCheckboxLink}>
              Terms of Use
            </a>
          </span>
        }
      />
      {formErrorMessages.submissionError !== '' && (
        <FormErrorMessage text={formErrorMessages.submissionError} className={styles.submissionErrorMessage} />
      )}
      <Button
        className={styles.submitButton}
        label={buttonText}
        theme="primary"
        size="medium"
        onClick={handleSubmission}
        disabled={buttonDisabled}></Button>
    </form>
  )
}

export default GatedContentForm
